// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useRef, useState } from 'react'
import { Sparkles, Send, RotateCcw } from 'lucide-react'
import * as api from '../../api/client'

type Turn = { role: 'user' | 'assistant'; content: string }

const QUICK = [
  'Cost of each drug across insurances, with # of Rx and last fill',
  'Margin by drug and dosage form',
  'Top payers by claim volume',
]

/**
 * Conversational report assistant. Each turn sends the whole conversation so the model
 * stays aware of the report being built; it returns a reply + a validated spec which we
 * apply to the builder. Falls back to the offline keyword router when the AI key isn't set.
 * Shows running API cost.
 */
export default function ReportAiChat({ pharmacyId, onApplySpec, onKeywordFallback }: {
  pharmacyId: string
  onApplySpec: (spec: api.ReportAiSpec) => void
  onKeywordFallback: (text: string) => void
}) {
  const [turns, setTurns] = useState<Turn[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  // D-CRX-61: the chat follows the provisioned key (reports/ai/status).
  const [reportAiChatEnabled, setReportAiChatEnabled] = useState(false)
  useEffect(() => { api.getReportAiStatus().then(s => setReportAiChatEnabled(!!s.ai_enabled)).catch(() => setReportAiChatEnabled(false)) }, [])
  const [note, setNote] = useState<string | null>(null)
  const [usage, setUsage] = useState<api.ReportAiUsage | null>(null)
  const [offline, setOffline] = useState(false)
  const [models, setModels] = useState<api.ReportAiModel[]>([])
  const [model, setModel] = useState<string>(() => {
    try { return localStorage.getItem('owlrx.reportai.model') || '' } catch { return '' }
  })
  const endRef = useRef<HTMLDivElement | null>(null)

  const refreshUsage = () => api.getReportAiUsage().then(setUsage).catch(() => {})
  useEffect(() => { refreshUsage() }, [])
  useEffect(() => {
    api.getReportAiModels().then(r => {
      setModels(r.models)
      setModel(prev => prev && r.models.some(m => m.id === prev) ? prev : r.default)
    }).catch(() => {})
  }, [])
  useEffect(() => { if (model) { try { localStorage.setItem('owlrx.reportai.model', model) } catch { /* ignore */ } } }, [model])
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [turns, busy])

  const send = async (text: string) => {
    const t = text.trim(); if (!t || busy) return
    const history: Turn[] = [...turns, { role: 'user', content: t }]
    setTurns(history); setInput(''); setBusy(true); setNote(null)
    try {
      const r = await api.reportAiChat(history.map(m => ({ role: m.role, content: m.content })), pharmacyId || undefined, model || undefined)
      setOffline(false)
      setTurns(h => [...h, { role: 'assistant', content: r.reply || 'Done.' }])
      if (r.parsed) onApplySpec(r.spec)
      if (r.dropped?.length) setNote(`Ignored fields not in the catalog: ${r.dropped.join(', ')}`)
      else if (!r.parsed) setNote("The model replied but didn't return a usable spec — try rephrasing.")
      refreshUsage()
    } catch (e: any) {
      if (e?.status === 501) {
        // AI key not configured → offline keyword router
        setOffline(true)
        setTurns(h => [...h, { role: 'assistant', content: 'AI key isn’t set, so I used the offline keyword matcher to pick the closest template. Add the key under the “AI assist key” card above for full chat.' }])
        onKeywordFallback(t)
      } else {
        const up = e?.body?.status
        const why = e?.body?.detail || e?.body?.error || e?.message || 'that failed'
        setTurns(h => [...h, { role: 'assistant', content: `Sorry — ${why}${up ? ` (Anthropic ${up})` : e?.status ? ` (HTTP ${e.status})` : ''}.` }])
      }
    } finally { setBusy(false) }
  }

  // Was a hard-coded `false` ("go-live risk reduction"), so the panel stayed off even with a key
  // provisioned. Owner 2026-08-09: it must follow the key. Now driven by reports/ai/status —
  // enabled only once a key actually resolves, off otherwise. The server still sends catalog field
  // names + the user's phrasing only; never PHI.
  if (!reportAiChatEnabled) {
    return (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <Sparkles size={15} color="#0c8f9c" />
          <span style={{ fontWeight: 700 }}>Report AI chat is off</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '6px 0 0' }}>
          The AI chat assistant is disabled. You can still build reports with the controls on the left.
          AI assistance is available in CareRx, where protected health information is screened before
          anything is sent externally.
        </p>
      </div>
    )
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <Sparkles size={15} color="#0c8f9c" />
        <span style={{ fontWeight: 700, fontSize: 13 }}>Build a report by chatting</span>
        {offline && <span style={pill}>offline keyword mode</span>}
        {models.length > 0 && (
          <select value={model} onChange={e => setModel(e.target.value)} title="AI model" style={modelSel} disabled={busy}>
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8a98a6' }}>
          {usage ? `AI cost — today $${usage.today.cost_usd.toFixed(4)} · month $${usage.month.cost_usd.toFixed(2)}` : ''}
        </span>
        {turns.length > 0 && (
          <button onClick={() => { setTurns([]); setNote(null) }} style={iconBtn} title="New conversation"><RotateCcw size={13} /></button>
        )}
      </div>

      {turns.length === 0 ? (
        <p style={{ fontSize: 12, color: '#5d6b7b', margin: '0 0 10px', lineHeight: 1.5 }}>
          Describe the report in plain words, then keep refining — “add last fill date”, “group by payer”,
          “only Cigna”. I stay aware of what we’re building. Structure-only: no PHI is sent.
        </p>
      ) : (
        <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10, paddingRight: 4 }}>
          {turns.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 7 }}>
              <div style={m.role === 'user' ? userBubble : aiBubble}>{m.content}</div>
            </div>
          ))}
          {busy && <div style={{ fontSize: 12, color: '#8a98a6', padding: '2px 4px' }}>thinking…</div>}
          <div ref={endRef} />
        </div>
      )}

      {note && <div style={{ fontSize: 11.5, color: '#8a6111', background: '#fff6e6', borderRadius: 7, padding: '7px 10px', marginBottom: 8 }}>{note}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(input) }}
          placeholder={turns.length ? 'Adjust it… (e.g. “add margin %”, “only last 90 days”)' : 'e.g. cost of each drug across insurances, # of Rx, last fill'}
          style={input_} disabled={busy} />
        <button onClick={() => send(input)} disabled={busy || !input.trim()} style={{ ...sendBtn, opacity: (busy || !input.trim()) ? 0.5 : 1 }}>
          <Send size={14} />
        </button>
      </div>

      {turns.length === 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
          {QUICK.map(q => <button key={q} onClick={() => send(q)} style={exchip}>{q}</button>)}
        </div>
      )}
    </div>
  )
}

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e4e9ef', borderRadius: 9, padding: '13px 15px', marginBottom: 14 }
const input_: React.CSSProperties = { flex: 1, border: '1px solid #e4e9ef', borderRadius: 8, padding: '9px 11px', fontSize: 13.5 }
const sendBtn: React.CSSProperties = { background: '#0c8f9c', color: '#fff', border: 0, padding: '0 14px', borderRadius: 8, cursor: 'pointer', display: 'grid', placeItems: 'center' }
const iconBtn: React.CSSProperties = { background: 'none', border: 0, color: '#8a98a6', cursor: 'pointer', display: 'grid', placeItems: 'center' }
const exchip: React.CSSProperties = { border: '1px solid #e4e9ef', background: '#fff', color: '#0a7681', fontSize: 11.5, padding: '4px 10px', borderRadius: 99, fontWeight: 600, cursor: 'pointer' }
const pill: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: '#8a6111', background: '#fff6e6', padding: '2px 7px', borderRadius: 99 }
const modelSel: React.CSSProperties = { fontSize: 11.5, border: '1px solid #e4e9ef', borderRadius: 7, padding: '3px 6px', color: '#16212e', background: '#fff', cursor: 'pointer', maxWidth: 220 }
const userBubble: React.CSSProperties = { background: '#0c8f9c', color: '#fff', padding: '7px 11px', borderRadius: '12px 12px 3px 12px', fontSize: 13, maxWidth: '78%', lineHeight: 1.45 }
const aiBubble: React.CSSProperties = { background: '#f1f5f8', color: '#16212e', padding: '7px 11px', borderRadius: '12px 12px 12px 3px', fontSize: 13, maxWidth: '85%', lineHeight: 1.45 }
